import React, {
  useState,
  createContext,
  useContext,
  useRef,
  useEffect,
} from 'react'
import env from '../../config/configuration'
import StreamingAvatar, {
  AvatarQuality,
  StreamingEvents,
  TaskMode,
  TaskType,
  VoiceEmotion,
} from '@heygen/streaming-avatar'
import { Outlet } from 'react-router-dom'

// Timer constants (in milliseconds)
const MAX_SESSION_DURATION = 10 * 60 * 1000 // 10 minutes
const IDLE_TIMEOUT = 60 * 1000 // 1 minute

const defaultAvatar = {
  gender: 'Male',
  region: 'South America',
  traits: 'Outgoing',
  method: 'Web',
  voiceId: '',
  bio: {
    greeting: 'Hello, I am a virtual assistant. How can I help you today?',
    operationalHours: {
      monday: { from: '08:00', to: '17:00', open: true },
      tuesday: { from: '08:00', to: '17:00', open: true },
      wednesday: { from: '08:00', to: '17:00', open: true },
      thursday: { from: '08:00', to: '17:00', open: true },
      friday: { from: '08:00', to: '17:00', open: true },
      saturday: { from: '08:00', to: '17:00', open: false },
      sunday: { from: '08:00', to: '17:00', open: false },
    },
  },
  userData: {
    firstName: 'John',
    lastName: 'Doe',
    email: 'johndoe@email.com',
  },
  faq: [],
}

const initAvatarPropsState = {
  avatarRef: {},
  sessionRef: {},
  startSession: async () => {},
  chat: [],
  setChat: () => {},
  speaking: '',
  setSpeaking: () => {},
  newChat: () => {},
  data: null,
  setData: () => {},
  isLoadingSession: false,
  isUserTalking: false,
  stream: null,
  endSession: () => {},
  voices: [],
  processing: false,
  startNewCall: async () => {},
  onCall: false,
}

const AvatarContext = createContext(initAvatarPropsState)

const useAvatar = () => {
  return useContext(AvatarContext)
}

async function fetchAccessToken() {
  const apiKey = env.HEYGEN_API
  const response = await fetch(
    'https://api.heygen.com/v1/streaming.create_token',
    {
      method: 'POST',
      headers: { 'x-api-key': apiKey },
    }
  )

  const { data } = await response.json()
  return data.token
}

function getKnowledgeBase(agent) {
  const getWorkingHours = () => {
    const days = [
      'Sunday',
      'Monday',
      'Tuesday',
      'Wednesday',
      'Thursday',
      'Friday',
      'Saturday',
    ]
    const hours = days.map((day) => {
      const { from, to, open } = agent.bio.operationalHours[day.toLowerCase()]
      return `${day}: ${open ? `${from} - ${to} ET` : 'Closed'}`.toUpperCase()
    })

    return hours.join('\n')
  }

  const getWorkingDaysRange = () => {
    const days = [
      'Sunday',
      'Monday',
      'Tuesday',
      'Wednesday',
      'Thursday',
      'Friday',
      'Saturday',
    ]
    const workingDays = days

      .filter((day) => agent.bio.operationalHours[day.toLowerCase()].open)
      .map((day) => day.slice(0, 3))
      .join('-')
    const lastDay = workingDays.split('-').pop()
    const firstDay = workingDays.split('-').shift()

    return `${firstDay}-${lastDay}`
  }

  const getWorkingHoursRange = () => {
    const days = [
      'Sunday',
      'Monday',
      'Tuesday',
      'Wednesday',
      'Thursday',
      'Friday',
      'Saturday',
    ]
    const hours = days
      .filter((day) => agent.bio.operationalHours[day.toLowerCase()].open)
      .map((day) => {
        const { from, to, open } = agent.bio.operationalHours[day.toLowerCase()]
        return `${from} - ${to}`
      })

    return hours.pop()
  }

  const getFAQs = () => {
    return agent.faq
      .map((faq, idx) => {
        return `${idx + 1}. **${faq.question}**\n   ${faq.answer}`
      })
      .join('\n')
  }
  // - **Monday-Friday, 8:00 AM - 5:00 PM ET**.
  //   const prompt = `

  //   Role and Objective

  // Role

  // You are Gabbi, an AI receptionist designed to assist law firms by handling client inquiries, collecting essential details, and providing information about legal services.

  // Objective

  // Your goal is to demonstrate how an AI receptionist can efficiently interact with potential clients, answer common questions, and simulate real client intake scenarios. Responses will be simulated to showcase the AI's capabilities.

  // Skills and Interaction

  // Professionalism & Engagement

  // Maintain a warm and professional tone.
  // Use natural phrasing and avoid robotic or scripted-sounding responses.
  // Adapt to different types of inquiries while staying within the legal domain.
  // Handling Working Hours

  // If asked about office hours, ${getWorkingHours()}.
  // Client Inquiry Handling

  // If asked about legal services, provide a general response: "Our firm handles a range of legal matters, including personal injury, family law, estate planning, and business law. What type of assistance are you looking for?"
  // If asked about consultation fees: "Many law firms offer free initial consultations, while others may have a small fee. I can guide you through what to expect based on your case."

  // Scripting

  // Introduction

  // Greeting:
  // "Hello ${agent.userData.firstName}, and thank you for calling ${
  //     agent.bio.companyName
  //   }! How can I assist you today?"
  // Understanding Their Needs:
  // "I'd be happy to help. Could you briefly describe your situation so I can provide the best guidance?"
  // If they mention a legal issue:
  // "That sounds like something our firm specializes in. Many clients have similar concerns, and we can certainly help. Would you like more details on how our attorneys handle cases like yours?"
  // Simulated Next Steps:
  // "In a real scenario, I would now assist you in scheduling a consultation or gathering necessary details for the attorney. For now, this demo is designed to show how AI can streamline your law firm's intake process."

  // Handling Common Objections

  // If the User Asks About AI Reliability:
  // "Great question! AI receptionists are designed to handle client inquiries 24/7, ensuring no leads are missed. Many firms find that using AI enhances client response times and improves overall efficiency."
  // If the User Expresses Skepticism:
  // "I understand—AI is a new tool for many firms. However, top law firms are already using AI to automate initial consultations, qualify leads, and reduce administrative workload. Would you like to see some case studies on how AI has helped firms like yours?"

  // Sales Hook

  // Before Ending the Conversation:
  // "Imagine never missing a client inquiry again—Gabbi is available 24/7 to assist potential clients, answer their questions, and ensure your firm captures every lead. Ready to see how this can work for your firm? Let's schedule a consultation to discuss your needs!"

  // If the User Expresses Interest:
  // "Fantastic! I can send you more details about how law firms are using AI to streamline their operations. What's the best email to reach you at?"

  // Closing

  // Standard Closing:
  // "Thank you for trying out this AI receptionist demo. If you'd like more information on how this technology can work for your firm, feel free to reach out. Have a great day!"
  // If the User Shows Interest in Subscription Options:
  // "We offer flexible monthly and yearly plans tailored for law firms of all sizes. If you'd like, I can send you a breakdown of pricing and features."

  // FAQs (Appendix)
  // ${getFAQs()}
  //     `

  const prompt = `

  Persona & Role
Avatar Name: ${agent?.gender == 'Male' ? 'Tyler' : 'Kristin'}
Gender: ${agent?.gender}
Role: AI receptionist for ${agent?.bio?.companyName}, a ${
    agent?.bio?.practiceArea != 'Other'
      ? agent?.bio?.practiceArea
      : agent?.bio?.practiceDesc
  } law firm located in ${agent?.bio?.address}.
Firm Details: Serves ${agent?.bio?.employees} employees, assisting ${
    agent?.bio?.clientType
  } (Individuals/Businesses).
Personality & Communication Style: ${
    agent?.bio?.personality
  }, with a Conversational approach.
Region & Language: Operating in ${agent?.region}, speaking ${
    agent?.bio?.language || 'English'
  } fluently.
Instructions & Response Framework
1. Greeting & First Interaction
Use ${agent?.bio?.greeting} in the first response only.
Example: "Hello! Welcome to ${
    agent?.bio?.companyName
  }. How can I assist you today?"
Avoid repeating the greeting in every response.
2. Response Guidelines
Keep replies under 3 sentences, each with a maximum of 50 words.
Maintain a ${
    agent?.bio?.personality
  } tone (e.g., "Sassy" → friendly with light humor; "Empathetic" → warm and understanding).
No robotic language. Use contractions where natural (e.g., "I’ll" instead of "I will").
Avoid stating "I am an AI" unless directly asked.
3. Understanding Client Needs Before Booking
Never immediately offer to book a consultation.
Instead, ask a follow-up question to understand the client's concern first.
Example:
Client: "I need help with a business contract dispute."
Avatar: "Got it! Our corporate law experts specialize in contract disputes. Could you share some details about your case so I can guide you better?"
(Only if the client confirms their need, then offer to book a meeting.)
4. Handling FAQs
Reference the FAQ List to answer common legal questions.
If a matching FAQ exists, respond concisely using the firm’s own wording.
If no FAQ is available, say:
"That’s a great question! I recommend speaking with one of our attorneys for the best guidance. Would you like to book a consultation?"
5. Booking Consultations & Call Transfers
If the office is open (Mon-Fri, 9 AM-6 PM):
"I can connect you to an attorney now. Let me check who's available!"
If the office is closed:
"Our office is currently closed, but I can schedule a consultation for you. What time works best?"
Example Interactions
Client: "Do you handle child custody cases?"
Avatar: "Yes! Our family law attorneys specialize in child custody matters. Would you like to share some details so we can match you with the right expert?"

Client: "How much do you charge for a divorce consultation?"
Avatar: "Our initial consultation fees depend on the complexity of the case. According to our policy: [Insert FAQ Answer]. Would you like me to book a session for you?"

FAQS:
${getFAQs()}
  `
  return prompt
}

const defautlChat = [
  // {
  //   message: 'Hello, I am a virtual assistant. How can I help you today?',
  //   type: 'avatar',
  // },
  // {
  //   message: 'Hello',
  //   type: 'user',
  // },
  // {
  //   message: 'Hello, I am a virtual assistant. How can I help you today?',
  //   type: 'avatar',
  // },
  // {
  //   message: 'Hello, I am a User assistant. How can I help you today?',
  //   type: 'user',
  // },
  // {
  //   message: 'Hello, I am a virtual assistant. How can I help you today?',
  //   type: 'avatar',
  // },
  // {
  //   message: 'Hello, I am a User assistant. How can I help you today?',
  //   type: 'user',
  // },
  // {
  //   message: 'Hello, I am a virtual assistant. How can I help you today?',
  //   type: 'avatar',
  // },
  // {
  //   message: 'Hello, I am a User assistant. How can I help you today?',
  //   type: 'user',
  // },
  // {
  //   message: 'Hello, I am a virtual assistant. How can I help you today?',
  //   type: 'avatar',
  // },
  // {
  //   message: 'Hello, I am a User assistant. How can I help you today?',
  //   type: 'user',
  // },
  // {
  //   message: 'Hello, I am a virtual assistant. How can I help you today?',
  //   type: 'avatar',
  // },
  // {
  //   message: 'Hello, I am a User assistant. How can I help you today?',
  //   type: 'user',
  // },
  // {
  //   message: 'Hello, I am a virtual assistant. How can I help you today?',
  //   type: 'avatar',
  // },
  // {
  //   message: 'Hello, I am a User assistant. How can I help you today?',
  //   type: 'user',
  // },
  // {
  //   message: 'Hello, I am a virtual assistant. How can I help you today?',
  //   type: 'avatar',
  // },
  // {
  //   message: 'Hello, I am a User assistant. How can I help you today?',
  //   type: 'user',
  // },
  // {
  //   message: 'Hello, I am a virtual assistant. How can I help you today?',
  //   type: 'avatar',
  // },
  // {
  //   message: 'Hello, I am a User assistant. How can I help you today?',
  //   type: 'user',
  // },
]

const AvatarProvider = ({ children }) => {
  const [speaking, setSpeaking] = useState('')
  const [chat, setChat] = useState(defautlChat)
  const [stream, setStream] = useState()
  const [data, setData] = useState()

  const avatarRef = useRef(null)
  const sessionRef = useRef(null)
  const avatarResponse = useRef(null)
  const [avatars, setAvatars] = useState([])
  const [voices, setVoices] = useState([])
  const [isLoadingSession, setIsLoadingSession] = useState(false)
  const [isUserTalking, setIsUserTalking] = useState(false)
  const [processing, setProcessing] = useState(false)
  const [onCall, setOnCall] = useState(false)
  const [lastActivity, setLastActivity] = useState(Date.now())
  const [sessionStartTime, setSessionStartTime] = useState(null)

  // Reset idle timer on user activity
  const handleUserActivity = () => {
    setLastActivity(Date.now())
  }

  // Session duration and idle timeout monitoring
  useEffect(() => {
    let sessionTimer
    let idleTimer

    if (onCall && sessionStartTime) {
      // Session duration timer
      sessionTimer = setInterval(() => {
        const currentTime = Date.now()
        const elapsedTime = currentTime - sessionStartTime

        if (elapsedTime >= MAX_SESSION_DURATION) {
          console.log('Maximum session duration reached (10 minutes)')
          endSession()
          clearInterval(sessionTimer)
        }
      }, 1000)

      // Idle timeout timer
      idleTimer = setInterval(() => {
        const currentTime = Date.now()
        const idleTime = currentTime - lastActivity

        if (idleTime >= IDLE_TIMEOUT) {
          console.log('Session idle timeout reached (1 minute)')
          endSession()
          clearInterval(idleTimer)
        }
      }, 1000)
    }

    // Cleanup timers
    return () => {
      clearInterval(sessionTimer)
      clearInterval(idleTimer)
    }
  }, [onCall, sessionStartTime, lastActivity])

  function newChat(message, type) {
    setChat((prev) => [
      ...prev,
      {
        message: message,
        type: type,
      },
    ])
  }

  const handleEndMessage = (event, type) => {
    console.log(type, ' stopped talking...')
    const speaking = avatarResponse.current
    if (speaking) {
      newChat(speaking, type)
      avatarResponse.current = ''
      setSpeaking('')
    }
  }

  function handleChatSpeaking(event) {
    const speaking = avatarResponse.current
    const message = (speaking || '') + event.detail.message
    console.log('Avatar is talking...')
    avatarResponse.current = message
    setSpeaking(message)
  }

  async function endSession() {
    await avatarRef.current?.stopAvatar()
    setStream(undefined)
    setChat([])
    setOnCall(false)
    setSessionStartTime(null) // Reset session start time
    setLastActivity(Date.now()) // Reset idle timer
  }

  function configureAvatar() {
    console.log('Configuring avatar...')
    if (!avatarRef.current) return
    const avatar = avatarRef.current
    avatar.on(StreamingEvents.AVATAR_END_MESSAGE, (e) =>
      handleEndMessage(e, 'avatar')
    )
    avatar.on(StreamingEvents.AVATAR_TALKING_MESSAGE, handleChatSpeaking)
    avatar.on(StreamingEvents.USER_TALKING_MESSAGE, handleChatSpeaking)
    avatar.on(StreamingEvents.USER_END_MESSAGE, (e) =>
      handleEndMessage(e, 'user')
    )

    avatar.on(StreamingEvents.STREAM_DISCONNECTED, () => {
      console.log('Stream disconnected')
      endSession()
    })
    avatar?.on(StreamingEvents.STREAM_READY, (event) => {
      console.log('>>>>> Stream ready:', event.detail)
      setStream(event.detail)
    })
    avatar?.on(StreamingEvents.USER_START, (event) => {
      console.log('>>>>> User started talking:', event)
      setIsUserTalking(true)
      handleUserActivity()
      // handleChatSpeaking(event)
    })
    avatar?.on(StreamingEvents.USER_STOP, (event) => {
      console.log('>>>>> User stopped talking:', event)
      setIsUserTalking(false)
    })
  }

  async function startSession() {
    setIsLoadingSession(true)
    const agent = JSON.parse(localStorage.getItem('agent')) || defaultAvatar
    const newToken = await fetchAccessToken()

    avatarRef.current = new StreamingAvatar({
      token: newToken,
    })

    configureAvatar()

    const emotions = {
      Outgoing: VoiceEmotion.FRIENDLY,
      Empathetic: VoiceEmotion.SOOTHING,
      Sassy: VoiceEmotion.EXCITED,
      'Southern Charm': VoiceEmotion.BROADCASTER,
    }

    try {
      const res = await avatarRef.current.createStartAvatar({
        quality: AvatarQuality.High,
        avatarName:
          agent.gender == 'Female' ? 'June_HR_public' : 'SilasHR_public',
        knowledgeBase: getKnowledgeBase(agent),
        voice: {
          voiceId: agent.voiceId,
          rate: 1, // 0.5 ~ 1.5
          emotion: emotions[data?.traits],
          // elevenlabsSettings: {
          //   stability: 1,
          //   similarity_boost: 1,
          //   style: 1,
          //   use_speaker_boost: false,
          // },
        },
        language: agent.bio.language || 'en',
        disableIdleTimeout: true,
      })

      setData(res)
      return res
      // await avatarRef.current.speak({
      //   text:
      //     agent.bio.greeting ||
      //     'Hello, I am a virtual assistant. How can I help you today?',
      //   sessionId: res.session_id,
      //   taskType: TaskType.REPEAT,
      //   taskMode: TaskMode.ASYNC,
      // })
      // await avatarRef.current?.startVoiceChat({
      //   useSilencePrompt: false,
      // })
    } catch (error) {
      console.error('Error starting avatar session:', error)
    } finally {
      setIsLoadingSession(false)
    }
  }

  async function startNewCall() {
    console.log('Starting new call...')
    setOnCall(true)
    setSessionStartTime(Date.now()) // Set session start time
    setLastActivity(Date.now()) // Reset idle timer
    const res = await startSession()
    const agent = JSON.parse(localStorage.getItem('agent')) || defaultAvatar
    await avatarRef.current?.startVoiceChat({
      useSilencePrompt: false,
    })
    await avatarRef.current.speak({
      text:
        // agent.bio.greeting ||
        'Hello, is this ' + agent?.userData?.firstName + '?',
      sessionId: res.session_id,
      taskType: TaskType.REPEAT,
      taskMode: TaskMode.ASYNC,
    })
  }

  const getAvatars = async () => {
    console.log('getting avatars...')
    const response = await fetch('/public-streaming-avatars.json', {
      method: 'GET',
      headers: { 'x-api-key': env.HEYGEN_API },
    })

    const { data } = await response.json()
    console.log('Avatars', data)
    setAvatars(data.avatar)
  }

  const getVoices = async () => {
    console.log('getting voices...')
    const response = await fetch('https://api.heygen.com/v2/voices', {
      method: 'GET',
      headers: { 'x-api-key': env.HEYGEN_API },
    })

    const { data } = await response.json()

    const result = await fetch('https://api.elevenlabs.io/v1/voices', {
      method: 'GET',
      headers: { 'x-api-key': env.ELEVEN_LAB_API },
    })

    const { voices } = await result.json()
    console.log(voices)

    const VoiceNamesList = [
      'Daniel',
      'Laura',
      'Matilda',
      'Jessica',
      'Sarah',
      'Alice',
      'Charlie',
      'Eric',
      'Lily',
      // 'Antoni',
      // 'Dorothy',
      // 'Josh',
      // 'Grace',
    ]

    const elevenLabVoice = voices
      .map((voice) => {
        if (VoiceNamesList.includes(voice.name))
          return {
            name: voice.name,
            gender: voice.labels.gender,
            preview: voice.preview_url,
          }
      })
      .filter((voice) => voice)

    const filteredVoices = data.voices.filter((voice) => {
      return VoiceNamesList.includes(voice.name) && voice.gender == 'unknown'
    })
    console.log('FilteredVoices', filteredVoices)

    console.log('ElevenLabVoice', elevenLabVoice)

    const finalVoices = elevenLabVoice.map((voice) => {
      const found = filteredVoices.find((v) => v.name == voice.name)
      if (found) {
        return {
          ...voice,
          voiceId: found.voice_id,
        }
      }
    })
    console.log('FinalVoices', finalVoices)

    setVoices(data.voices)
    console.log('Voices', response)
  }

  const onLoad = async () => {
    setProcessing(true)
    await getAvatars()
    // await getVoices()
    setProcessing(false)
  }

  useEffect(() => {
    onLoad()
    // Add event listeners for user activity
    window.addEventListener('mousemove', handleUserActivity)
    window.addEventListener('keydown', handleUserActivity)
    window.addEventListener('click', handleUserActivity)

    return () => {
      endSession()
      // Cleanup
      window.removeEventListener('mousemove', handleUserActivity)
      window.removeEventListener('keydown', handleUserActivity)
      window.removeEventListener('click', handleUserActivity)
    }
  }, [])

  return (
    <AvatarContext.Provider
      value={{
        avatarRef,
        sessionRef,
        startSession,
        chat,
        setChat,
        speaking,
        setSpeaking,
        newChat,
        data,
        stream,
        setData,
        isLoadingSession,
        isUserTalking,
        avatars,
        endSession,
        voices,
        processing,
        startNewCall,
        onCall,
      }}
    >
      <Outlet />
    </AvatarContext.Provider>
  )
}

export { AvatarProvider, useAvatar }
